// Company-level financials for the CFO view. Figures are stated in whole
// dollars so every table can format them consistently.

export type MonthlyFinancials = {
  label: string
  grossRecovery: number
  brandPayouts: number
  netRevenue: number
  operatingCosts: number
}

export const monthlyFinancials: MonthlyFinancials[] = [
  { label: 'Jan', grossRecovery: 412_800, brandPayouts: 268_320, netRevenue: 144_480, operatingCosts: 118_900 },
  { label: 'Feb', grossRecovery: 448_600, brandPayouts: 291_590, netRevenue: 157_010, operatingCosts: 121_400 },
  { label: 'Mar', grossRecovery: 507_300, brandPayouts: 329_745, netRevenue: 177_555, operatingCosts: 129_800 },
  { label: 'Apr', grossRecovery: 561_900, brandPayouts: 365_235, netRevenue: 196_665, operatingCosts: 134_200 },
  { label: 'May', grossRecovery: 618_400, brandPayouts: 401_960, netRevenue: 216_440, operatingCosts: 141_700 },
  { label: 'Jun', grossRecovery: 674_100, brandPayouts: 438_165, netRevenue: 235_935, operatingCosts: 146_500 },
]

export type BrandEconomics = {
  brand: string
  grossRecovery: number
  brandPayout: number
  netRevenue: number
  takeRate: number
  contributionMargin: number
  status: 'Active' | 'Onboarding' | 'Paused'
}

export const brandEconomics: BrandEconomics[] = [
  { brand: 'Eddy', grossRecovery: 325_875, brandPayout: 211_819, netRevenue: 114_056, takeRate: 35, contributionMargin: 21.4, status: 'Active' },
  { brand: 'Nisolo', grossRecovery: 371_488, brandPayout: 241_467, netRevenue: 130_021, takeRate: 35, contributionMargin: 24.8, status: 'Active' },
  { brand: '1 People', grossRecovery: 148_260, brandPayout: 96_369, netRevenue: 51_891, takeRate: 35, contributionMargin: 18.2, status: 'Active' },
  { brand: 'Montce', grossRecovery: 96_400, brandPayout: 62_660, netRevenue: 33_740, takeRate: 35, contributionMargin: 16.9, status: 'Active' },
  { brand: 'Nature Baby', grossRecovery: 54_120, brandPayout: 35_178, netRevenue: 18_942, takeRate: 35, contributionMargin: 12.4, status: 'Onboarding' },
  { brand: 'Lili Blanc', grossRecovery: 28_940, brandPayout: 18_811, netRevenue: 10_129, takeRate: 35, contributionMargin: 9.6, status: 'Paused' },
]

export type CostLine = {
  category: string
  amount: number
  detail: string
}

export const costStructure: CostLine[] = [
  { category: 'Fulfillment & 3PL', amount: 214_600, detail: 'Receiving, storage, pick and pack' },
  { category: 'Marketing', amount: 118_400, detail: '10% allocation against projected recovery' },
  { category: 'Payment & marketplace fees', amount: 92_800, detail: 'Processing plus channel commissions' },
  { category: 'Payroll & contractors', amount: 268_900, detail: 'Operations, merchandising, engineering' },
  { category: 'Facilities & rent', amount: 74_500, detail: 'Warehouse and office leases' },
  { category: 'Software & platform', amount: 23_200, detail: 'Tooling, hosting, data services' },
]

export type PartnerAccount = {
  partner: string
  type: '3PL' | 'Facility'
  location: string
  monthlyCost: number
  outstanding: number
  terms: string
  status: 'Current' | 'Due soon' | 'Overdue'
}

export const partnerAccounts: PartnerAccount[] = [
  { partner: 'ShipCalm', type: '3PL', location: 'Carlsbad, CA', monthlyCost: 68_400, outstanding: 34_200, terms: 'Net 30', status: 'Current' },
  { partner: 'Ottawa Logistics', type: '3PL', location: 'Ottawa, ON', monthlyCost: 41_900, outstanding: 41_900, terms: 'Net 15', status: 'Due soon' },
  { partner: 'AaryaShip', type: '3PL', location: 'Newark, NJ', monthlyCost: 37_200, outstanding: 0, terms: 'Net 30', status: 'Current' },
  { partner: 'Fulfillment House', type: '3PL', location: 'Columbus, OH', monthlyCost: 29_800, outstanding: 29_800, terms: 'Net 30', status: 'Overdue' },
  { partner: 'Shipvine', type: '3PL', location: 'Louisville, KY', monthlyCost: 21_400, outstanding: 10_700, terms: 'Net 30', status: 'Current' },
]

export type LeaseAccount = {
  site: string
  purpose: string
  monthlyRent: number
  sqft: number
  termEnds: string
}

export const leaseAccounts: LeaseAccount[] = [
  { site: 'Nashville, TN', purpose: 'Primary recovery warehouse', monthlyRent: 38_500, sqft: 42_000, termEnds: 'Mar 2029' },
  { site: 'Los Angeles, CA', purpose: 'Studio and live selling', monthlyRent: 21_800, sqft: 8_400, termEnds: 'Sep 2027' },
  { site: 'New York, NY', purpose: 'Commercial office', monthlyRent: 14_200, sqft: 3_100, termEnds: 'Jun 2028' },
]

export type LedgerPosition = {
  label: string
  amount: number
  detail: string
  tone: 'in' | 'out' | 'neutral'
}

export type FinancialAccount = {
  name: string
  institution: string
  type: 'Checking' | 'Savings' | 'Credit card' | 'Payment processor'
  mask: string
  balance: number
  lastSynced: string
  status: 'Connected' | 'Needs reauth'
}

export const financialAccounts: FinancialAccount[] = [
  { name: 'Operating account', institution: 'Mercury', type: 'Checking', mask: '4821', balance: 862_400, lastSynced: '12 minutes ago', status: 'Connected' },
  { name: 'Reserve account', institution: 'Mercury', type: 'Savings', mask: '7730', balance: 422_200, lastSynced: '12 minutes ago', status: 'Connected' },
  { name: 'Corporate card', institution: 'Ramp', type: 'Credit card', mask: '9114', balance: -84_300, lastSynced: '1 hour ago', status: 'Connected' },
  { name: 'Marketplace settlements', institution: 'Stripe', type: 'Payment processor', mask: '2065', balance: 138_900, lastSynced: '3 hours ago', status: 'Needs reauth' },
]

export const cashPosition: LedgerPosition[] = [
  { label: 'Cash on hand', amount: 1_284_600, detail: 'Operating and reserve accounts', tone: 'neutral' },
  { label: 'Accounts receivable', amount: 486_200, detail: '$118,400 over 30 days', tone: 'in' },
  { label: 'Brand payouts due', amount: 342_900, detail: 'Next run Jun 30', tone: 'out' },
  { label: 'Partner payables', amount: 116_600, detail: '$29,800 overdue', tone: 'out' },
]
